import React, { useCallback, useEffect, useState } from 'react';
import { GOOGLE_REVIEWS_SEARCH_URL } from '../constants/googleReviewsUrl';
import { useStorefrontSettings } from '../context/StorefrontSettingsContext';
import { formatGoogleReviewStars, useGoogleReviewsFeed } from '../hooks/useGoogleReviewsFeed';
import './FloatingReviewPopup.css';

const DISMISS_KEY = 'raheasy_floating_review_popup_dismissed_at';
const DISMISS_COOLDOWN_MS = 2 * 24 * 60 * 60 * 1000;
const SHOW_DELAY_MS = 6000;
const ROTATE_MS = 10000;

const isDismissCooldownActive = () => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = parseInt(raw, 10);
    if (!Number.isFinite(at)) return false;
    return Date.now() - at < DISMISS_COOLDOWN_MS;
  } catch {
    return false;
  }
};

const markDismissed = () => {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
};

const FloatingReviewPopup = () => {
  const { showGoogleReviewBadge } = useStorefrontSettings();
  const { reviews, landingPageUrl, isLive, loaded } = useGoogleReviewsFeed();
  const [visible, setVisible] = useState(false);
  const [index, setIndex] = useState(0);

  const handleClose = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setVisible(false);
    markDismissed();
  }, []);

  useEffect(() => {
    if (!showGoogleReviewBadge || !loaded || !isLive || reviews.length === 0) return undefined;
    if (isDismissCooldownActive()) return undefined;

    const showTimer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(showTimer);
  }, [showGoogleReviewBadge, loaded, isLive, reviews.length]);

  useEffect(() => {
    if (!visible || reviews.length <= 1) return undefined;

    const rotateTimer = setInterval(() => {
      setIndex((prev) => (prev + 1) % reviews.length);
    }, ROTATE_MS);

    return () => clearInterval(rotateTimer);
  }, [visible, reviews.length]);

  if (!showGoogleReviewBadge || !visible || reviews.length === 0) return null;

  const review = reviews[index];
  const stars = formatGoogleReviewStars(review.stars);
  const linkUrl = review.sourceUrl || landingPageUrl || GOOGLE_REVIEWS_SEARCH_URL;

  const popupBody = (
    <>
      <div className="floating-review-popup-header">
        <div className="floating-review-popup-source">
          <span className="floating-review-popup-google-g" aria-hidden="true">G</span>
          <span>Recent Google Review</span>
          <span className="floating-review-popup-stars" aria-hidden="true">{stars}</span>
        </div>
      </div>
      <div className="floating-review-popup-body">
        <p className="floating-review-popup-text">&ldquo;{review.text}&rdquo;</p>
        <span className="floating-review-popup-author">- {review.author || 'Google user'}</span>
      </div>
    </>
  );

  return (
    <aside
      className="floating-review-popup"
      role="complementary"
      aria-label="Recent Google review"
    >
      <button
        type="button"
        className="floating-review-popup-close"
        onClick={handleClose}
        aria-label="Close review popup"
      >
        ×
      </button>
      <a
        href={linkUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="floating-review-popup-link"
        aria-label="View this Google review"
      >
        {popupBody}
      </a>
    </aside>
  );
};

export default FloatingReviewPopup;
